import { ArrowUpRight, Path, Stop, ArrowClockwise } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  TagBadge,
  Text,
  TextArea,
} from '@chatsdet/ui';
import { useState } from 'react';
import { API_URL } from '../../../config';
import { safeHref } from '../../../utils/safeHref';
import {
  getTicketingProviderColor,
  getTicketingProviderLabel,
} from '../../../utils/ticketingProviders';
import { ExploratoryRunHistoryControls } from '../ExploratoryRunHistoryControls';
import { deriveHeaderVerdict, HEADER_VERDICT_META, type PlanItem } from '../ExploratoryTestVerdict';
import type { ExploratoryTestData } from './types';

type ExploratoryDetailHeaderProps = {
  test: ExploratoryTestData;
  title: string;
  parentId: string | null;
  currentRunId: string;
  isInFlight: boolean;
  onCancel: () => void;
  isCancelPending: boolean;
  onRetest: (instructions?: string) => void;
  isRetestPending: boolean;
  retestError: string | null;
};

export function ExploratoryDetailHeader({
  test,
  title,
  parentId,
  currentRunId,
  isInFlight,
  onCancel,
  isCancelPending,
  onRetest,
  isRetestPending,
  retestError,
}: ExploratoryDetailHeaderProps) {
  const [retestOpen, setRetestOpen] = useState(false);
  const [instructions, setInstructions] = useState(test.instructions);
  const verdict = deriveHeaderVerdict(
    (test.reportJson?.testPlan ?? []) as PlanItem[],
    (test.reportJson?.appended ?? []) as PlanItem[]
  );

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 min-h-14 border-b border-border bg-background">
      <div className="flex items-baseline gap-3 min-w-0 flex-1">
        <Text weight="semibold" className="text-base truncate shrink-0">
          {title}
        </Text>
        {verdict && (
          <TagBadge
            name={HEADER_VERDICT_META[verdict].label}
            color={HEADER_VERDICT_META[verdict].color}
            size="sm"
            hideDot
            className="shrink-0 self-center"
          />
        )}
        {test.targetKind === 'sandbox-v2' && test.sandboxV2TestedSha && (
          <TagBadge
            name={`Tested ${test.sandboxV2TestedSha.slice(0, 8)}`}
            color="blue"
            size="sm"
            hideDot
            tooltip={`This report applies to PR head ${test.sandboxV2TestedSha}`}
          />
        )}
        {test.sourceAdhocTestId && (
          <Link
            to="/exploratory/$exploratoryTestId"
            params={{ exploratoryTestId: test.sourceAdhocTestId }}
            className="text-xs text-muted-foreground hover:underline"
          >
            Retest of earlier run
          </Link>
        )}
        {test.externalTicketProvider && test.externalTicketIdentifier && (
          <a
            href={safeHref(test.externalTicketUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 self-center"
            aria-label={`Open ${test.externalTicketIdentifier} on ${getTicketingProviderLabel(test.externalTicketProvider)}`}
          >
            <TagBadge
              name={test.externalTicketIdentifier}
              color={getTicketingProviderColor(test.externalTicketProvider)}
              size="sm"
              hideDot
              tooltip={`${getTicketingProviderLabel(test.externalTicketProvider)} · ${test.externalTicketIdentifier}`}
            />
          </a>
        )}
      </div>

      <Flex align="center" gap="sm" className="shrink-0">
        {test.targetKind === 'sandbox-v2' && test.githubPrNumber && test.githubRepoFullName && (
          <DialogTrigger
            isOpen={retestOpen}
            onOpenChange={(open) => {
              if (open) setInstructions(test.instructions);
              setRetestOpen(open);
            }}
            size="sm"
          >
            <Button variant="secondary" size="sm" iconLeft={<ArrowClockwise size={16} />}>
              Retest PR
            </Button>
            <Dialog>
              <DialogContent>
                <div className="space-y-4">
                  <div>
                    <Text weight="semibold" className="block">
                      Retest {test.githubRepoFullName}#{test.githubPrNumber}
                    </Text>
                    <Text size="sm" className="text-muted-foreground">
                      Creates a new run against the latest pull request head, with fresh sandbox
                      copies.
                    </Text>
                  </div>
                  <TextArea
                    label="Scenario instructions"
                    value={instructions}
                    onChange={setInstructions}
                    rows={5}
                  />
                  {retestError && (
                    <Text size="sm" className="text-destructive">
                      {retestError}
                    </Text>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="secondary" onPress={() => setRetestOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    isPending={isRetestPending}
                    isDisabled={isRetestPending || instructions.trim().length === 0}
                    onPress={() => onRetest(instructions.trim())}
                  >
                    Start retest
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </DialogTrigger>
        )}
        {test.promotedWorkflowId && (
          <Link
            to="/flows/$workflowId"
            params={{ workflowId: test.promotedWorkflowId }}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            View flow
            <ArrowUpRight size={14} />
          </Link>
        )}
        {test.traceStorageKey && (
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Path size={16} />}
            tooltip="Open the Playwright trace (network, DOM snapshots, console) in the trace viewer"
            onPress={() =>
              window.open(
                // window.open can't send the org header — carry it as a query param.
                `${API_URL}/adhoc-tests/${currentRunId}/trace`,
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            View trace
          </Button>
        )}
        <ExploratoryRunHistoryControls
          parentId={parentId}
          currentRunId={currentRunId}
          isInFlight={isInFlight}
        />
        {isInFlight && (
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tone="destructive"
            tooltip="Cancel run"
            iconLeft={<Stop size={16} />}
            onPress={onCancel}
            isPending={isCancelPending}
          />
        )}
      </Flex>
    </div>
  );
}
